import { useEffect, useState } from 'react';

/** Two rounds is enough to read as dealing; thirteen would be a loading screen. */
const ROUNDS = 2;

export interface DealRitualProps {
  handId: string;
  enabled?: boolean;
  /** How many hands are being dealt, so the cards go where the players are. */
  seats?: number;
}

/**
 * A short, non-blocking dealer moment whenever a new hand reaches the table.
 *
 * It deals *to the seats*: one card per player per round, going round the table
 * in turn order, each landing where that player actually sits. The previous
 * version sprayed seven cards outward in a fan, which reads as an effect rather
 * than as a deal — and at six seats it was visibly dealing to nobody.
 *
 * Nothing here is on the interaction path: the board is live underneath and the
 * whole thing is `pointer-events: none`.
 */
export function DealRitual({ handId, enabled = true, seats = 4 }: DealRitualProps) {
  const [visible, setVisible] = useState(true);
  const n = Math.max(2, Math.min(6, seats));

  useEffect(() => {
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), 1400);
    return () => window.clearTimeout(timer);
  }, [handId]);

  if (!enabled || !visible) return null;

  const cards = Array.from({ length: n * ROUNDS }, (_, i) => {
    const seat = i % n;
    const round = Math.floor(i / n);
    // Seat 0 is the player, at the bottom; everyone else spreads across the arc
    // above, in the order they sit.
    const mine = seat === 0;
    const spread = n > 1 ? (seat - 1) / Math.max(1, n - 2 || 1) - 0.5 : 0;
    return {
      key: i,
      // Dealt in turn order, so the eye can follow one card at a time.
      delay: i * 74,
      x: mine ? 0 : Math.round(spread * 2 * 190),
      y: mine ? 128 : -96,
      tilt: mine ? 0 : Math.round(spread * 2 * 14),
      round,
    };
  });

  return (
    <div className="deal-ritual" aria-hidden="true">
      {cards.map(({ key, delay, x, y, tilt, round }) => (
        <i
          key={key}
          className="deal-ritual__card"
          style={
            {
              '--deal-delay': `${delay}ms`,
              '--deal-x': `${x}px`,
              '--deal-y': `${y}px`,
              '--deal-tilt': `${tilt}deg`,
              // The second round lands slightly proud of the first, so the two
              // do not stack into one card.
              '--deal-offset': `${round * 5}px`,
            } as React.CSSProperties
          }
        />
      ))}
      {/* The cut card turning over, which is the moment the hand acquires its
          wild rank and the only part of the deal that is information. */}
      <i className="deal-ritual__wild">✦</i>
    </div>
  );
}
